import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { getTransfer } from "@/lib/transfersApi";

interface TransferViewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transferId?: number;
}

const TransferViewModal = ({ open, onOpenChange, transferId }: TransferViewModalProps) => {
  const { toast } = useToast();
  const [transfer, setTransfer] = useState<any>(null);

  useEffect(() => {
    if (open && transferId) {
      loadTransferData();
    }
  }, [open, transferId]);

  const loadTransferData = async () => {
    if (!transferId) return;
    
    try {
      const data = await getTransfer(transferId);
      setTransfer(data);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatVehicleType = (type: string) => {
    const types: Record<string, string> = {
      'driver': 'Driver',
      'guide': 'Guide',
      'staff': 'Staff',
      'luggage_car': 'Luggage Car'
    };
    return types[type] || type;
  };

  const formatPricingType = (type: string) => {
    const types: Record<string, string> = {
      'per_person': 'Per Person',
      'per_family': 'Per Family',
      'per_group': 'Per Group'
    };
    return types[type] || type;
  };

  if (!transfer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>View Transfer Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {transfer.image_url && (
            <div className="w-full">
              <img 
                src={transfer.image_url} 
                alt={transfer.title}
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>
          )}

          <div className="flex gap-2">
            <Badge variant="secondary">{formatVehicleType(transfer.type)}</Badge>
            <Badge variant="outline">{formatPricingType(transfer.pricing_type)}</Badge>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Transfer Code</Label>
              <Input value={transfer.transfer_code || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Emirate</Label>
              <Input value={transfer.emirate || ""} disabled />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={transfer.title || ""} disabled />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={transfer.description || ""} disabled rows={3} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Capacity</Label>
              <Input value={transfer.capacity?.toString() || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Supplier Name</Label>
              <Input value={transfer.supplier_name || ""} disabled />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cost Supplier</Label>
              <Input value={transfer.cost_supplier?.toString() || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Tax Percentage</Label>
              <Input value={transfer.tax_percentage?.toString() || ""} disabled />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Price OP</Label>
              <Input value={transfer.price_op?.toString() || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Price AG</Label>
              <Input value={transfer.price_ag?.toString() || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Price CF</Label>
              <Input value={transfer.price_cf?.toString() || ""} disabled />
            </div>
          </div>

          {transfer.hcte_notes && (
            <div className="space-y-2">
              <Label>HCTE Notes</Label>
              <Textarea value={transfer.hcte_notes} disabled rows={3} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransferViewModal;
